import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component, SyntheticEvent } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Tab, TabProps } from 'semantic-ui-react';
import { AutomaticBillItem, AutomaticBillRunDto, BillXmlRequestDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import styled from 'styled-components';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { getAutomaticBillXmlRequests } from 'services/mediDataServices';
import MediDataUploadTableView from 'components/medidata-upload/MediDataUploadTableView';
import moment from 'moment';
import { millisToMinutesAndSeconds } from 'utils/fomattingUtils';
import { BillXmlStatus } from 'ts-types/api.enums';
import { PxDropdownSc } from 'components/final-form/Select';


const TabContainer = styled(Tab)`
  flex: 1 0 300px;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;

  .text.menu {
    flex: 0 0 auto;
    margin: 0;
  }

  .active.tab {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    margin: 0;
    padding: 0;
    border: none;
    box-shadow: none;
  }
`;

const VerticalFlexDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 1.5rem;
  row-gap: 0.5rem;
  justify-content: start;

  padding: 0.5rem;
`;

const InnerInfoGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 1.5rem;
  row-gap: 0.5rem;
  justify-content: start;

  padding: 0.5rem;

  &.filterBy {
    grid-template-columns: auto;
  }

  .label {
    justify-self: right;
  }

  .value {
    font-weight: bold;
  }
`;


interface AutomaticBillItemWithError {
  treatmentId: number;
  errorText: string;
}

interface Props extends MessageSourceProps {
  toggleModal: () => void,
  onClose: () => void,
  runId?: number,
  automaticBillRun?: AutomaticBillRunDto,
  setHeaderMessage: (headerMessage: string) => void,
  setErrorMessage: (errorMessage: string) => void
}

interface State {
  automaticBillItems: Array<AutomaticBillItem>,
  automaticBillItemsWithError: Array<AutomaticBillItemWithError>,
  xmlBillsKey: number,
  xmlBillsKeyInt: number,
  xmlBillsLoaded: boolean,
  xmlBills: Array<BillXmlRequestDto>,
  filteredXmlBills: Array<BillXmlRequestDto>,
  showFilter: boolean,
  cancelTokenSource: CancelTokenSource
}

class AutomaticBillDetails extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const {automaticBillRun, setHeaderMessage} = props;

    const automaticBillItems = automaticBillRun && automaticBillRun.automaticBillItems
        ? automaticBillRun.automaticBillItems
        : [];

    let automaticBillItemsWithError: Array<AutomaticBillItemWithError> = [];

    if (automaticBillItems.length > 0) {
      automaticBillItems.forEach(item => {
        if (item.error) {
          const itemWithError: AutomaticBillItemWithError = {
            treatmentId: item.treatmentId,
            errorText: item.xmlError
          };
          automaticBillItemsWithError.push(itemWithError);
        }
      })
    }
    if (automaticBillRun) {
      const runId = automaticBillRun.id;
      const billDate = automaticBillRun.billDate;
      const headerMessage = ` > ${runId} (${moment(billDate).format("DD.MM.YYYY")})`
      setHeaderMessage(headerMessage);
    }

    this.state = {
      automaticBillItems: automaticBillItems,
      automaticBillItemsWithError: automaticBillItemsWithError,
      xmlBillsKey: 1,
      xmlBillsKeyInt: 1,
      xmlBillsLoaded: false,
      xmlBills: [],
      filteredXmlBills: [],
      showFilter: false,
      cancelTokenSource
    }
  }

  _cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 25
  });

  fetchBillXmls = (event: SyntheticEvent, data: TabProps) => {
    const {setErrorMessage, getMessage} = this.props;


    if (data.activeIndex !== 1) {
      this.setShowFilter(false);
      return;
    }

    if (data.activeIndex === 1) {
      this.setShowFilter(true);
    }

    this.setState({
      xmlBillsLoaded: false
    });

    getAutomaticBillXmlRequests(this.props.runId!, this.state.cancelTokenSource)
        .then(response => {
          this.setState({
            xmlBillsKey: this.state.xmlBillsKey + 1,
            xmlBills: response,
            filteredXmlBills: response
          });
        }).catch(() => {
          setErrorMessage(getMessage("error.general"));
        })
        .finally(() => {
          this.setState({
            xmlBillsLoaded: true
          });
        })

  };

  onBillsUpdated = (updatedBills: Array<BillXmlRequestDto>) => {

    const updatedBillsMap: { [key: number]: BillXmlRequestDto } = {};
    updatedBills.forEach(bill => {
      updatedBillsMap[bill.id] = bill;
    });

    const currentState = this.state;
    const nextBills = currentState.xmlBills.map(bill => (updatedBillsMap[bill.id] || bill));
    const nextFilteredBills = currentState.filteredXmlBills.map(bill => (updatedBillsMap[bill.id] || bill));

    this.setState({
      xmlBillsKeyInt: (currentState.xmlBillsKeyInt + 1),
      xmlBills: nextBills,
      filteredXmlBills: nextFilteredBills
    })
  };

  setShowFilter = (showFilter: boolean) => {
    this.setState({
      showFilter
    })
  };

  filterXmlBills = (filter: string) => {
    const currentState = this.state;
    let filteredXmlBills = currentState.xmlBills;
    if (filter !== "ALL") {
      filteredXmlBills = currentState.xmlBills.filter(b => filter === b.status);
    }

    this.setState({
      xmlBillsKeyInt: (currentState.xmlBillsKeyInt + 1),
      filteredXmlBills: filteredXmlBills
    });
  }

  render(): React.ReactNode {

    const {automaticBillRun, getMessage} = this.props;
    const {automaticBillItems, automaticBillItemsWithError, showFilter} = this.state;

    const numPrinted = automaticBillItems.filter(item => item.docPrinted).length;
    const numXml = automaticBillItems.filter(item => item.xmlCreated).length;
    const numErrors = automaticBillItemsWithError.length;

    const billXmlStatus = Object.values(BillXmlStatus);
    let statusOptions: Array<DropdownOption> = billXmlStatus.map(xmlStatus => {
      return {
        key: xmlStatus,
        text: getMessage(`transmissionStatus.${xmlStatus}`),
        value: xmlStatus
      }
    });
    const allOption = {
      key: "ALL",
      text: getMessage("paymentType.ALL"),
      value: "ALL"
    }
    statusOptions = [allOption, ...statusOptions]

    const panes = [
      {
        menuItem: 'Errors',
        render: () => <Tab.Pane attached={false}>
          {this.renderItemWithErrorSegment()}
        </Tab.Pane>
      },
      {
        menuItem: 'Medidata',
        render: () => {
          const {xmlBillsKey, xmlBillsKeyInt, filteredXmlBills, xmlBillsLoaded} = this.state;
          return <Tab.Pane attached={false}>
            <MediDataUploadTableView
                key={xmlBillsKey}
                billsKeyInt={xmlBillsKeyInt}
                bills={filteredXmlBills}
                billsTariffId={1}
                billsLoading={!xmlBillsLoaded}
                onBillsUpdated={this.onBillsUpdated}
            />
          </Tab.Pane>;
        }
      }
    ]

    const info = [
      {label: getMessage("automaticBilling.printedInvoices"), value: numPrinted},
      {label: getMessage("automaticBilling.xmlInvoices"), value: numXml},
      {label: getMessage("automaticBilling.skippedTreatments"), value: numErrors}
    ]

    const automaticBillRunDetails = automaticBillRun && [
      {
        label: getMessage("bill.date"),
        value: moment(automaticBillRun.billDate).format("DD.MM.YYYY")
      },
      {
        label: getMessage("automaticBilling.timeOfExecution"),
        value: moment(automaticBillRun.timeOfExecution).format("DD.MM.YYYY H:mm:ss")
      },
      {
        label: getMessage("automaticBilling.responseTime"),
        value: millisToMinutesAndSeconds(automaticBillRun.responseTime)
      }
    ]

    return (
        <VerticalFlexDiv>
          <InfoGrid>
            <InnerInfoGrid>
              {

                info.map((infoItem, ix) =>
                    <React.Fragment key={`inforow-${ix}`}>
                      <div className="label">{infoItem.label}:</div>
                      <div className="value">{infoItem.value}</div>
                    </React.Fragment>
                )
              }
            </InnerInfoGrid>
            {automaticBillRunDetails &&
            <InnerInfoGrid>
              {
                automaticBillRunDetails.map((infoItem, ix) =>
                    <React.Fragment key={`inforow-${ix}`}>
                      <div className="label">{infoItem.label}:</div>
                      <div className="value">{infoItem.value}</div>
                    </React.Fragment>
                )
              }
            </InnerInfoGrid>
            }
            {showFilter &&
            <InnerInfoGrid className="filterBy">
              <div>{getMessage("mediData.filterByStatus")}:</div>
              <div>
                <PxDropdownSc
                    selection
                    options={statusOptions}
                    onChange={(e: any, data: any) => this.filterXmlBills(data.value)}
                />
              </div>
              <div></div>
            </InnerInfoGrid>
            }
          </InfoGrid>

          <TabContainer
              menu={{secondary: true, pointing: true}}
              panes={panes}
              onTabChange={this.fetchBillXmls}
          />
        </VerticalFlexDiv>
    );
  };

  dynamicColumnCellRenderer = ({dataKey, parent, rowIndex, cellData}: any) => {

    return (
        <CellMeasurer
            cache={this._cache}
            columnIndex={0}
            key={dataKey}
            parent={parent}
            rowIndex={rowIndex}>
          <div
              style={{
                padding: '10px 0',
                color: 'darkred',
                whiteSpace: 'normal'
              }}>
            {cellData}
          </div>
        </CellMeasurer>
    );
  };

  itemWithErrorRowGetter = ({index}: any) => {
    const {automaticBillItemsWithError} = this.state;

    Object.assign(automaticBillItemsWithError[index], {index: index + 1});

    return automaticBillItemsWithError[index]
  };

  itemWithErrorRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderItemWithErrorSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {automaticBillItemsWithError} = this.state;

    return (
        <VirtualizedTable
            rowCount={automaticBillItemsWithError.length}
            rowGetter={this.itemWithErrorRowGetter}
            rowRenderer={this.itemWithErrorRowRenderer}
            rowHeight={this._cache.rowHeight}
            columns={[
              {
                width: 60,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("automaticBilling.treatmentId")),
                dataKey: "treatmentId"
              },
              {
                width: 350,
                flexGrow: 1,
                label: (getMessage("automaticBilling.errorText")),
                dataKey: "errorText",
                cellRenderer: this.dynamicColumnCellRenderer
              }
            ]}
        />
    );
  };

}

export default withMessages(AutomaticBillDetails);
